import Icon from '@components/Icon';
import Typography from '@components/Typography';
import { useFieldValidation } from '@ef-core/useFieldValidation';
import { type Validation } from '@ef-core/validation';
import { concatStyles } from '@styles/domain';
import { type FC, useId } from 'react';
import * as styles from './index.css';

interface Props {
  value?: string;
  onChange: (value: string) => void;
  validate?: (value?: string) => Validation | undefined;
  label?: string;
  type?: 'text' | 'email';
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

const Input: FC<Props> = ({
  value,
  onChange,
  validate,
  label,
  type = 'text',
  id,
  name,
  placeholder,
  disabled,
  required,
  className,
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const { validation, displayError, markTouched, markDirty } =
    useFieldValidation({ value, validate });

  return (
    <span className={concatStyles([styles.field, className])}>
      {label && (
        <label htmlFor={inputId}>
          <Typography variant='body3'>{label}</Typography>
          {required && (
            <span aria-hidden='true' className={styles.required}>
              *
            </span>
          )}
        </label>
      )}
      <span className={styles.inputWrap}>
        <input
          id={inputId}
          type={type}
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          aria-required={required || undefined}
          value={value}
          onChange={(event) => {
            markDirty();
            onChange(event.target.value);
          }}
          onBlur={markTouched}
          className={concatStyles([
            styles.input,
            displayError && styles.inputError,
          ])}
        />
        {validation && (
          <Icon
            name={displayError ? 'close' : 'check'}
            size='sm'
            color={displayError ? 'error' : 'success'}
            className={styles.validationIcon}
          />
        )}
      </span>
      {displayError && validation?.error && (
        <Typography variant='body3' role='alert' className={styles.error}>
          {validation.error}
        </Typography>
      )}
    </span>
  );
};

export default Input;
